// /src/components/admin/UserManagementTab.jsx
// User Management - View, suspend, or edit users
// Required for legal/police compliance

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { RefreshCw, Search, Calendar, MapPin } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { logAdminAction, auditFailureMessage } from '../../utils/adminAuditLog';
import ConfirmationModal from '../core/ConfirmationModal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { formatTextWithStyles } from '../../utils/formatText';

const IMAGE_SIZES = { S: '33%', M: '66%', L: '100%' };

const renderArticleContent = (content, inlineImages = []) => {
  if (!content) return null;
  const styleBase = { color: '#374151', lineHeight: '1.625', marginBottom: '16px' };
  const hasMarkers = /\[(IMAGE|IMG):[^\]]+\]/.test(content);
  if (!hasMarkers) {
    return formatTextWithStyles(content, 'ac-', styleBase);
  }
  const parts = content.split(/\[(?:IMAGE|IMG):([^\]]+)\]/);
  return parts.map((part, index) => {
    if (index % 2 === 0) {
      if (!part.trim()) return null;
      return formatTextWithStyles(part, `ac-${index}-`, styleBase);
    }
    const image = inlineImages.find(img => img.id === part);
    if (image) {
      const width = IMAGE_SIZES[image.size] || '100%';
      return (
        <div key={`img-${index}`} style={{ margin: '16px 0' }}>
          <img src={image.displayUrl} alt="" loading="lazy" style={{ width, maxWidth: '100%', height: 'auto', borderRadius: '8px' }} />
        </div>
      );
    }
    return null;
  });
};

const getVideoEmbed = (url) => {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId;
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
    } else if (url.includes('youtube.com')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }
    if (videoId) return `https://www.youtube-nocookie.com/embed/${videoId}`;
  }
  if (url.includes('vimeo.com')) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match && match[1]) return `https://player.vimeo.com/video/${match[1]}`;
  }
  if (url.includes('framatube.org') || url.includes('peertube')) {
    try {
      const urlObj = new URL(url);
      const origin = urlObj.origin;
      let videoPath;
      if (url.includes('/w/')) videoPath = url.split('/w/')[1].split('?')[0];
      else if (url.includes('/videos/watch/')) videoPath = url.split('/videos/watch/')[1].split('?')[0];
      if (videoPath) return `${origin}/videos/embed/${videoPath}`;
    } catch (e) { /* unrecognized */ }
  }
  return null;
};

const UserManagementTab = () => {
  const theme = useTheme();
  const { windowWidth } = useMediaQuery();
  const { t, formattingLocale } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 20;
  const [suspendModal, setSuspendModal] = useState({ visible: false, user: null });
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [unsuspendModal, setUnsuspendModal] = useState({ visible: false, user: null });
  // Shows a failed audit write after a suspend or unsuspend that succeeded.
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showAuditFailure = (audit) => {
    const message = auditFailureMessage(t, audit);
    if (message) setInfoModal({ visible: true, message });
  };
  const [articlesModal, setArticlesModal] = useState({ visible: false, user: null, articles: [], loading: false });
  const [articleTypeFilter, setArticleTypeFilter] = useState('both');
  const [previewArticle, setPreviewArticle] = useState(null);
  const [articleCounts, setArticleCounts] = useState({});

  const searchTermRef = useRef('');
  const skipPageEffect = useRef(false);

  const loadUsers = async (pageNum = 0, search = '') => {
    setLoading(true);
    try {
      const queryOpts = {
        select: 'id,email,display_name,created_at,is_active',
        order: 'created_at.desc',
        limit: pageSize,
        offset: pageNum * pageSize
      };
      const countOpts = { select: 'id', limit: 10000 };

      // Add server-side search filter when a search term is present
      if (search) {
        const pattern = `*${search}*`;
        const orFilter = `email.ilike.${pattern},display_name.ilike.${pattern}`;
        queryOpts.or = orFilter;
        countOpts.or = orFilter;
      }

      const [{ data, error }, { data: allIds }, { data: articleData }] = await Promise.all([
        supabaseFetch.query('users', queryOpts),
        supabaseFetch.query('users', countOpts),
        supabaseFetch.query('articles', { select: 'author_id', limit: 10000 })
      ]);

      if (error) {
        console.error('Error loading users:', error);
        return;
      }

      const counts = {};
      (articleData || []).forEach(a => {
        if (a.author_id) counts[a.author_id] = (counts[a.author_id] || 0) + 1;
      });

      setUsers(data || []);
      setArticleCounts(counts);
      setTotalPages(Math.ceil((allIds?.length || 0) / pageSize));
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipPageEffect.current) {
      skipPageEffect.current = false;
      return;
    }
    loadUsers(page, searchTermRef.current);
  }, [page]);

  // Debounced server-side search: reset to page 0 and reload when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      searchTermRef.current = searchTerm;
      // Skip the page effect since we're already loading here
      if (page !== 0) skipPageEffect.current = true;
      setPage(0);
      loadUsers(0, searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch event_details when previewing an event article
  useEffect(() => {
    if (!previewArticle || previewArticle.article_type !== 'event' || previewArticle._eventDetailsFetched) return;
    supabaseFetch.query('event_details', {
      select: 'start_date,end_date,venue_name,venue_address,venue_postcode',
      eq: { article_id: previewArticle.id },
      single: true
    }).then(({ data }) => {
      setPreviewArticle(prev => prev ? ({ ...prev, ...(data || {}), _eventDetailsFetched: true }) : null);
    });
  }, [previewArticle?.id]);

  const handleSuspend = (userId, currentStatus) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    if (currentStatus) {
      // Currently active — open suspend modal
      setSuspendReason('');
      setSuspendModal({ visible: true, user });
    } else {
      // Currently suspended — open unsuspend modal
      setUnsuspendModal({ visible: true, user });
    }
  };

  const confirmSuspend = async () => {
    if (!suspendReason.trim()) return;
    setSuspending(true);
    try {
      const { error } = await supabaseFetch.update('users', {
        is_active: false,
        suspended_at: new Date().toISOString(),
        suspension_reason: suspendReason.trim()
      }, { eq: { id: suspendModal.user.id } });
      if (error) { console.error('Error suspending user:', error); return; }
      const audit = await logAdminAction({
        actionType: 'user_suspend',
        targetType: 'user',
        targetId: suspendModal.user.id,
        reason: suspendReason.trim()
      });
      setSuspendModal({ visible: false, user: null });
      setSuspendReason('');
      loadUsers(page, searchTermRef.current);
      showAuditFailure(audit);
    } catch (err) {
      console.error('Error suspending user:', err);
    } finally {
      setSuspending(false);
    }
  };

  const confirmUnsuspend = async () => {
    try {
      const { error } = await supabaseFetch.update('users', {
        is_active: true,
        suspended_at: null,
        suspension_reason: null
      }, { eq: { id: unsuspendModal.user.id } });
      if (error) { console.error('Error unsuspending user:', error); return; }
      const audit = await logAdminAction({
        actionType: 'user_unsuspend',
        targetType: 'user',
        targetId: unsuspendModal.user.id
      });
      setUnsuspendModal({ visible: false, user: null });
      loadUsers(page, searchTermRef.current);
      showAuditFailure(audit);
    } catch (err) {
      console.error('Error unsuspending user:', err);
    }
  };

  const handleViewProfile = (userId) => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'profile', userId }
    }));
  };

  const handleSeeArticles = async (user) => {
    setArticleTypeFilter('both');
    setArticlesModal({ visible: true, user, articles: [], loading: true });
    try {
      const { data } = await supabaseFetch.query('articles', {
        select: 'id,title,article_type,is_published,published_at,created_at,cover_image_url,video_url,inline_images,summary,content',
        eq: { author_id: user.id },
        order: 'created_at.desc',
        limit: 100
      });
      setArticlesModal(prev => ({ ...prev, articles: data || [], loading: false }));
    } catch (err) {
      console.error('Error loading user articles:', err);
      setArticlesModal(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    searchBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: 1,
      maxWidth: '400px'
    },
    searchInput: {
      flex: 1,
      padding: '8px 12px',
      borderRadius: '6px',
      border: `1px solid ${theme?.border || '#DDD'}`,
      fontSize: '13px'
    },
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
      marginBottom: '20px'
    },
    th: {
      textAlign: 'left',
      padding: '12px 8px',
      borderBottom: `2px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.secondary || '#666',
      fontWeight: '600'
    },
    td: {
      padding: '12px 8px',
      borderBottom: `1px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.primary || '#1A1A1A'
    },
    actionButton: {
      padding: '4px 10px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      marginRight: '6px',
      fontWeight: '500'
    },
    viewButton: {
      backgroundColor: theme?.primary || '#024B99',
      color: 'white'
    },
    suspendButton: (isActive) => ({
      backgroundColor: isActive ? '#fee2e2' : '#dcfce7',
      color: isActive ? '#991b1b' : '#15803d'
    }),
    editButton: {
      backgroundColor: theme?.bg || '#F5F5F5',
      color: theme?.text?.primary || '#1A1A1A'
    },
    statusBadge: (isActive) => ({
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '500',
      backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
      color: isActive ? '#15803d' : '#991b1b'
    }),
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      marginTop: '20px'
    },
    pageButton: (disabled) => ({
      padding: '6px 12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: disabled ? theme?.bg || '#F5F5F5' : theme?.primary || '#024B99',
      color: disabled ? theme?.text?.secondary || '#666' : 'white',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: '13px'
    }),
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{t('userManagement')}</div>
        <div style={styles.searchBox}>
          <Search size={16} color={theme?.text?.secondary || '#666'} />
          <input
            type="text"
            placeholder={t('searchByEmailOrName')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <button
          onClick={() => loadUsers(page, searchTerm)}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'default' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
      </div>

      {loading ? (
        <div style={styles.loadingBox}>{t('loadingUsers')}</div>
      ) : users.length === 0 ? (
        <div style={styles.loadingBox}>{t('noUsersFound')}</div>
      ) : (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t('displayName')}</th>
                <th style={styles.th}>{t('email')}</th>
                <th style={styles.th}>{t('joined')}</th>
                <th style={styles.th}>{t('active')}</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Articles</th>
                <th style={styles.th}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td style={styles.td}>{user.display_name || t('noName')}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>
                    {new Date(user.created_at).toLocaleDateString(formattingLocale)}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(user.is_active !== false)}>
                      {user.is_active !== false ? t('active') : t('suspended')}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600', color: articleCounts[user.id] ? '#1F2937' : '#9CA3AF' }}>
                    {articleCounts[user.id] || 0}
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleViewProfile(user.id)}
                      style={{ ...styles.actionButton, ...styles.viewButton }}
                    >
                      {t('viewProfile')}
                    </button>
                    <button
                      onClick={() => handleSeeArticles(user)}
                      style={{ ...styles.actionButton, backgroundColor: '#DBEAFE', color: '#1E40AF' }}
                    >
                      See Articles
                    </button>
                    <button
                      onClick={() => handleSuspend(user.id, user.is_active !== false)}
                      style={{ ...styles.actionButton, ...styles.suspendButton(user.is_active !== false) }}
                    >
                      {user.is_active !== false ? t('suspend') : t('unsuspend')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={styles.pagination}>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              style={styles.pageButton(page === 0)}
            >
              {t('previous')}
            </button>
            <span style={{ fontSize: '13px', color: theme?.text?.secondary || '#666' }}>
              {t('pageOfPages', { current: page + 1, total: totalPages })}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              style={styles.pageButton(page >= totalPages - 1)}
            >
              {t('next')}
            </button>
          </div>
        </>
      )}

      {/* Suspend Modal */}
      {suspendModal.visible && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#991b1b' }}>{t('suspendUserConfirm')}</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#555' }}>
              {suspendModal.user?.display_name || suspendModal.user?.email}
            </p>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#1A1A1A' }}>
              {t('reason')}
            </label>
            <textarea
              autoFocus
              value={suspendReason}
              onChange={e => setSuspendReason(e.target.value)}
              placeholder={t('suspendUserPrompt')}
              rows={6}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #DDD', fontSize: '13px', fontFamily: 'OpenSauceSans', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <p style={{ margin: '8px 0 16px 0', fontSize: '12px', color: '#888' }}>{t('suspendUserNotice')}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setSuspendModal({ visible: false, user: null })}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#e5e7eb', color: '#1A1A1A', fontSize: '13px', cursor: 'pointer' }}
              >
                {t('cancel') || 'Cancel'}
              </button>
              <button
                onClick={confirmSuspend}
                disabled={!suspendReason.trim() || suspending}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: !suspendReason.trim() || suspending ? '#fca5a5' : '#dc2626', color: 'white', fontSize: '13px', fontWeight: '600', cursor: !suspendReason.trim() || suspending ? 'default' : 'pointer' }}
              >
                {suspending ? t('suspending') || 'Suspending…' : t('suspend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsuspend Confirmation Modal */}
      <ConfirmationModal
        visible={unsuspendModal.visible}
        title={t('unsuspendUserConfirm')}
        message={unsuspendModal.user?.display_name || unsuspendModal.user?.email || ''}
        okLabel={t('unsuspend')}
        cancelLabel={t('cancel') || 'Cancel'}
        onOk={confirmUnsuspend}
        onCancel={() => setUnsuspendModal({ visible: false, user: null })}
      />

      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
      />

      {/* See Articles Modal — responsive list layout */}
      {articlesModal.visible && (
        <div
          onClick={() => setArticlesModal({ visible: false, user: null, articles: [], loading: false })}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: windowWidth < 640 ? 'stretch' : 'center', justifyContent: 'center', padding: windowWidth < 640 ? '0' : '24px 16px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: '#EDF2F8', borderRadius: windowWidth < 640 ? '0' : '12px', width: '100%', maxWidth: windowWidth < 640 ? 'none' : '768px', height: windowWidth < 640 ? '100%' : '80vh', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A' }}>
                  {articlesModal.user?.display_name || articlesModal.user?.email}
                </div>
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>Articles</div>
              </div>
              <button
                onClick={() => setArticlesModal({ visible: false, user: null, articles: [], loading: false })}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6B7280', lineHeight: 1, padding: '0 2px' }}
              >
                ×
              </button>
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', background: 'white', padding: '8px', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              {[{ value: 'news', label: 'News' }, { value: 'event', label: 'Events' }, { value: 'both', label: 'Both' }].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '13px', fontFamily: 'OpenSauceSans', fontWeight: '600', color: '#1a1a1a' }}>
                  <input
                    type="radio"
                    name="adminArticleTypeFilter"
                    value={opt.value}
                    checked={articleTypeFilter === opt.value}
                    onChange={e => setArticleTypeFilter(e.target.value)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {/* Article list */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
              {articlesModal.loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontSize: '14px' }}>Loading articles…</div>
              ) : articlesModal.articles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontSize: '14px' }}>No articles found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {articlesModal.articles.filter(a => articleTypeFilter === 'both' || a.article_type === articleTypeFilter).map(article => (
                    <div
                      key={article.id}
                      style={{ background: 'white', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}
                    >
                      {/* Top row: type badge + title + View button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px' }}>
                        <div style={{
                          padding: '3px 6px',
                          background: article.article_type === 'event' ? '#BB4D9A' : '#024B99',
                          borderRadius: '3px',
                          fontSize: '9px',
                          fontWeight: '700',
                          color: 'white',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          flexShrink: 0
                        }}>
                          {article.article_type === 'event' ? 'EVENT' : 'NEWS'}
                        </div>
                        <h3 style={{
                          margin: 0,
                          flex: 1,
                          fontSize: '14px',
                          fontFamily: 'OpenSauceSans',
                          fontWeight: '600',
                          color: '#1a1a1a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {article.title}
                        </h3>
                        <button
                          onClick={() => setPreviewArticle(article)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF',
                            flexShrink: 0
                          }}
                        >
                          View
                        </button>
                      </div>

                      {/* Bottom row: status / date / views */}
                      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '6px 8px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>Status</div>
                          <div style={{ fontSize: '11px', fontFamily: 'OpenSauceSans', fontWeight: '600', color: article.is_published ? '#1a1a1a' : '#ef4444' }}>
                            {article.is_published ? 'Published' : 'Draft'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>Date</div>
                          <div style={{ fontSize: '11px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                            {formatDate(article.published_at || article.created_at)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>Type</div>
                          <div style={{ fontSize: '11px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                            {article.article_type === 'event' ? 'Event' : 'News'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Article preview modal — responsive, matches ArticleDetail layout */}
      {previewArticle && (
        <div
          onClick={() => setPreviewArticle(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10002, display: 'flex', alignItems: windowWidth < 640 ? 'stretch' : 'center', justifyContent: 'center', padding: windowWidth < 640 ? '0' : '24px 16px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: windowWidth < 640 ? '0' : '12px', width: '100%', maxWidth: windowWidth < 640 ? 'none' : '768px', height: windowWidth < 640 ? '100%' : '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden' }}
          >
            {/* Admin header bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Article Preview</span>
              <button
                onClick={() => setPreviewArticle(null)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6B7280', lineHeight: 1, padding: '0 2px' }}
              >
                ×
              </button>
            </div>

            {windowWidth < 640 ? (
              /* Mobile: single column, full-screen scrollable */
              <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#F8FAFC' }}>
                {previewArticle.video_url ? (
                  getVideoEmbed(previewArticle.video_url) ? (
                    <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative' }}>
                      <iframe src={getVideoEmbed(previewArticle.video_url)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen title="Article Video" />
                    </div>
                  ) : (
                    <div style={{ background: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '8px', padding: '12px', margin: '16px 16px 0' }}>
                      <p style={{ fontSize: '13px', color: '#856404', margin: '0 0 6px 0' }}><strong>Video link:</strong></p>
                      <a href={previewArticle.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066CC', fontSize: '12px', wordBreak: 'break-all' }}>{previewArticle.video_url}</a>
                    </div>
                  )
                ) : previewArticle.cover_image_url ? (
                  <div style={{ width: '66%', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                    <img src={previewArticle.cover_image_url} alt={previewArticle.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ) : null}
                <div style={{ padding: '16px' }}>
                  <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px 0', lineHeight: '1.3' }}>
                    {previewArticle.title}
                  </h1>
                  {previewArticle.summary && (
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                      {previewArticle.summary}
                    </p>
                  )}
                  {previewArticle.article_type === 'event' && (previewArticle.start_date || previewArticle.venue_name) && (
                    <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {previewArticle.start_date && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                          <Calendar size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                          <div>
                            <div>{new Date(previewArticle.start_date).toLocaleDateString(formattingLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                            <div style={{ fontSize: '13px' }}>
                              {new Date(previewArticle.start_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}
                              {previewArticle.end_date && ` – ${new Date(previewArticle.end_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}`}
                            </div>
                          </div>
                        </div>
                      )}
                      {previewArticle.venue_name && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                          <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: '500' }}>{previewArticle.venue_name}</div>
                            {previewArticle.venue_address && <div style={{ fontSize: '13px' }}>{previewArticle.venue_address}</div>}
                            {previewArticle.venue_postcode && <div style={{ fontSize: '13px' }}>{previewArticle.venue_postcode}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    {renderArticleContent(previewArticle.content, previewArticle.inline_images)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '20px', marginBottom: '24px' }}>End</div>
                  <div style={{ borderTop: '1px solid #E5E7EB', margin: '16px 0' }} />
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '10px', color: '#6B7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Author</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: theme?.primary || '#024B99', marginBottom: '4px' }}>{articlesModal.user?.display_name || '—'}</div>
                    {previewArticle.created_at && (
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {new Date(previewArticle.created_at).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setPreviewArticle(null)} style={{ width: '100%', padding: '12px', backgroundColor: theme?.primary || '#024B99', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
              /* Desktop/Tablet: two-column layout matching ArticleDetail */
              <>
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                  {/* Left sidebar — image, title, author, date */}
                  <div style={{ width: '45%', minWidth: '45%', borderRight: '1px solid #E5E7EB', overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC' }}>
                    {!previewArticle.video_url && previewArticle.cover_image_url && (
                      <div style={{ width: '66%', aspectRatio: '4 / 3', overflow: 'hidden', borderRadius: '8px', marginBottom: '16px' }}>
                        <img src={previewArticle.cover_image_url} alt={previewArticle.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', lineHeight: '1.3', margin: '0 0 20px 0' }}>
                      {previewArticle.title}
                    </h1>
                    <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px' }}>
                      <div style={{ fontSize: '10px', color: '#6B7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Author</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: theme?.primary || '#024B99', marginBottom: '8px' }}>{articlesModal.user?.display_name || '—'}</div>
                      {previewArticle.created_at && (
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          {new Date(previewArticle.created_at).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right content area — video (if any), summary, event details, body */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {previewArticle.video_url && (
                      getVideoEmbed(previewArticle.video_url) ? (
                        <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative', marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
                          <iframe src={getVideoEmbed(previewArticle.video_url)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen title="Article Video" />
                        </div>
                      ) : (
                        <div style={{ background: '#FFF3CD', border: '1px solid #FFEAA7', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                          <p style={{ fontSize: '13px', color: '#856404', margin: '0 0 6px 0' }}><strong>Video link:</strong></p>
                          <a href={previewArticle.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066CC', fontSize: '12px', wordBreak: 'break-all' }}>{previewArticle.video_url}</a>
                        </div>
                      )
                    )}
                    {previewArticle.summary && (
                      <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                        {previewArticle.summary}
                      </p>
                    )}
                    {previewArticle.article_type === 'event' && (previewArticle.start_date || previewArticle.venue_name) && (
                      <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {previewArticle.start_date && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                            <Calendar size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                            <div>
                              <div>{new Date(previewArticle.start_date).toLocaleDateString(formattingLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                              <div style={{ fontSize: '13px' }}>
                                {new Date(previewArticle.start_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}
                                {previewArticle.end_date && ` – ${new Date(previewArticle.end_date).toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit' })}`}
                              </div>
                            </div>
                          </div>
                        )}
                        {previewArticle.venue_name && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748B', fontSize: '14px' }}>
                            <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                            <div>
                              <div style={{ fontWeight: '500' }}>{previewArticle.venue_name}</div>
                              {previewArticle.venue_address && <div style={{ fontSize: '13px' }}>{previewArticle.venue_address}</div>}
                              {previewArticle.venue_postcode && <div style={{ fontSize: '13px' }}>{previewArticle.venue_postcode}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      {renderArticleContent(previewArticle.content, previewArticle.inline_images)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '20px' }}>End</div>
                  </div>
                </div>
                {/* Footer close button */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', flexShrink: 0, backgroundColor: '#F9FAFB' }}>
                  <button onClick={() => setPreviewArticle(null)} style={{ width: '100%', padding: '10px', backgroundColor: theme?.primary || '#024B99', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
